import { useEffect, useState } from 'preact/hooks';

/**
 * Reads the paging state off the scroll container.
 *
 * @param {Element} element carousel container element
 * @param {number} [assumedScrollLeft] position to measure against instead of
 * the current one, used while a smooth scroll is still animating
 * @returns {object} pages, page, pageWidth, maxScroll, atStart and atEnd
 */
export const measure = (element, assumedScrollLeft) => {
    // RTL counts away from zero, so paging works on the distance travelled
    const scrollLeft = Math.abs(
        typeof assumedScrollLeft === 'number'
            ? assumedScrollLeft
            : element.scrollLeft
    );
    const itemWidth = element.children[0]?.offsetWidth || 0;

    if (!itemWidth) {
        return {
            pages: 1,
            page: 0,
            pageWidth: 0,
            maxScroll: 0,
            atStart: true,
            atEnd: true
        };
    }

    const gap = parseFloat(getComputedStyle(element).columnGap) || 0;
    // an item only fits if the gap in front of it fits too
    const perPage = Math.max(
        1,
        Math.floor((element.clientWidth + gap) / (itemWidth + gap))
    );
    const pageWidth = perPage * (itemWidth + gap);
    const maxScroll = Math.max(0, element.scrollWidth - element.clientWidth);
    // count the reachable scroll stops, not the options: flex-shrunk options
    // round to widths that imply pages the track cannot actually scroll to
    const pages = maxScroll < 1 ? 1 : 1 + Math.ceil(maxScroll / pageWidth);
    const atEnd = scrollLeft >= maxScroll;

    return {
        pages,
        pageWidth,
        maxScroll,
        atStart: scrollLeft <= 0,
        atEnd,
        // the last page is short whenever the options don't divide evenly, so
        // clamp rather than round past the final indicator
        page: atEnd
            ? pages - 1
            : Math.min(pages - 1, Math.round(scrollLeft / pageWidth))
    };
};

/**
 * Paging shared by the horizontal option carousels (Top Three, Predictions),
 * which render different markup around identical scroll behaviour.
 *
 * @param {object} elementRef reference to the scrolling options container
 * @returns {object} state and handlers for the carousel controls
 */
export const useCarousel = (elementRef) => {
    const isRTL = document.documentElement.lang === 'ar';
    const [leftArrowDisable, setLeftArrowDisable] = useState(true);
    const [rightArrowDisable, setRightArrowDisable] = useState(false);
    const [pageIndex, setPageIndex] = useState(0);
    const [pageCount, setPageCount] = useState(1);

    const sync = (assumedScrollLeft) => {
        const element = elementRef.current;
        if (!element) {
            return;
        }

        const { pages, page, atStart, atEnd } = measure(
            element,
            assumedScrollLeft
        );

        setPageCount(pages);
        setPageIndex(page);
        setLeftArrowDisable(isRTL ? atEnd : atStart);
        setRightArrowDisable(isRTL ? atStart : atEnd);
    };

    useEffect(() => {
        const element = elementRef.current;
        if (!element) {
            return null;
        }

        sync();

        const onScrollEnd = () => sync();
        element.addEventListener('scrollend', onScrollEnd);

        // measuring only on mount left these stale after a rotation or resize
        let observer;
        if (typeof ResizeObserver === 'function') {
            observer = new ResizeObserver(() => sync());
            observer.observe(element);
        }

        return () => {
            element.removeEventListener('scrollend', onScrollEnd);
            observer?.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [elementRef, elementRef.current, isRTL]);

    const scrollByPage = (direction) => {
        const element = elementRef.current;
        if (!element) {
            return;
        }

        const { pageWidth, maxScroll } = measure(element);
        if (!pageWidth) {
            return;
        }

        // page off the live position rather than a running index, so swiping
        // and then pressing an arrow moves one page from where the fan is
        const current = Math.abs(element.scrollLeft);
        const target =
            direction > 0
                ? Math.min(
                      maxScroll,
                      (Math.floor(current / pageWidth) + 1) * pageWidth
                  )
                : Math.max(0, (Math.ceil(current / pageWidth) - 1) * pageWidth);

        element.scrollTo({
            left: isRTL ? -target : target,
            behavior: 'smooth'
        });
        sync(target);
    };

    const isIndicatorActive = (index) => index === pageIndex;

    return {
        leftArrowDisable,
        rightArrowDisable,
        pageCount,
        isIndicatorActive,
        scrollByPage
    };
};
